import React, { useState, useEffect } from 'react';
import { X, User, Trophy } from 'lucide-react';
import { MatchInfo, Screen, ScoringType } from '../../types';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface EditMatchModalProps {
  isOpen: boolean;
  matchInfo: MatchInfo;
  currentScreen: Screen;
  onClose: () => void;
  onSave: (matchInfo: Partial<MatchInfo>) => void;
}

export function EditMatchModal({ isOpen, matchInfo, currentScreen, onClose, onSave }: EditMatchModalProps) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    playerA: matchInfo.playerA,
    playerB: matchInfo.playerB,
    scoringType: matchInfo.scoringType,
    competitionName: matchInfo.competitionName,
    categoryName: matchInfo.categoryName,
    drawName: matchInfo.drawName,
    courtName: matchInfo.courtName || '',
    refereeName: matchInfo.refereeName || '',
    roundType: matchInfo.roundType || ''
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        playerA: matchInfo.playerA,
        playerB: matchInfo.playerB,
        scoringType: matchInfo.scoringType,
        competitionName: matchInfo.competitionName,
        categoryName: matchInfo.categoryName,
        drawName: matchInfo.drawName,
        courtName: matchInfo.courtName || '',
        refereeName: matchInfo.refereeName || '',
        roundType: matchInfo.roundType || ''
      });
    }
  }, [isOpen, matchInfo]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const updatedInfo: Partial<MatchInfo> = {
      playerA: formData.playerA.trim(),
      playerB: formData.playerB.trim(),
      competitionName: formData.competitionName.trim(),
      categoryName: formData.categoryName.trim(),
      drawName: formData.drawName.trim(),
      courtName: formData.courtName.trim() || undefined,
      refereeName: formData.refereeName.trim() || undefined,
      roundType: formData.roundType || undefined
    };

    // Only include scoringType if we're on the 'before' screen
    if (currentScreen === 'before') {
      updatedInfo.scoringType = formData.scoringType as ScoringType;
    }

    onSave(updatedInfo);
    onClose();
  };

  const isScoringTypeDisabled = currentScreen === 'during';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('modals.editMatch.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 transition-colors"
            aria-label={t('common.close')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4">
          {/* Players */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                <User className="w-4 h-4 inline mr-1" />
                {t('screens.setup.fields.playerA')}
              </label>
              <input
                type="text"
                value={formData.playerA}
                onChange={(e) => handleInputChange('playerA', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                <User className="w-4 h-4 inline mr-1" />
                {t('screens.setup.fields.playerB')}
              </label>
              <input
                type="text"
                value={formData.playerB}
                onChange={(e) => handleInputChange('playerB', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
          </div>

          {/* Scoring Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              <Trophy className="w-4 h-4 inline mr-1" />
              {t('screens.setup.fields.scoringType')}
            </label>
            <select
              value={formData.scoringType}
              onChange={(e) => handleInputChange('scoringType', e.target.value)}
              disabled={isScoringTypeDisabled}
              className={`w-full px-3 py-2 border-2 transition-colors ${
                isScoringTypeDisabled
                  ? 'border-gray-300 bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white'
              }`}
            >
              <option value="BestOf3">{t('screens.setup.options.bestOf3')}</option>
              <option value="BestOf5">{t('screens.setup.options.bestOf5')}</option>
            </select>
            {isScoringTypeDisabled && (
              <p className="mt-1 text-xs text-gray-500 italic">
                {t('modals.editMatch.scoringTypeDisabled')}
              </p>
            )}
          </div>

          {/* Competition & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t('screens.setup.fields.competition')}
              </label>
              <input
                type="text"
                value={formData.competitionName}
                onChange={(e) => handleInputChange('competitionName', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t('screens.setup.fields.category')}
              </label>
              <input
                type="text"
                value={formData.categoryName}
                onChange={(e) => handleInputChange('categoryName', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
          </div>

          {/* Draw & Court */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t('screens.setup.fields.drawName')}
              </label>
              <input
                type="text"
                value={formData.drawName}
                onChange={(e) => handleInputChange('drawName', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t('screens.setup.fields.courtName')}
              </label>
              <input
                type="text"
                value={formData.courtName}
                onChange={(e) => handleInputChange('courtName', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
          </div>

          {/* Round Type & Referee */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t('screens.setup.fields.roundType')}
              </label>
              <select
                value={formData.roundType}
                onChange={(e) => handleInputChange('roundType', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white transition-colors"
              >
                <option value="">{t('screens.setup.options.roundTypes.none')}</option>
                <option value="pool">{t('screens.setup.options.roundTypes.pool')}</option>
                <option value="r32">{t('screens.setup.options.roundTypes.r32')}</option>
                <option value="r16">{t('screens.setup.options.roundTypes.r16')}</option>
                <option value="qf">{t('screens.setup.options.roundTypes.qf')}</option>
                <option value="sf">{t('screens.setup.options.roundTypes.sf')}</option>
                <option value="final">{t('screens.setup.options.roundTypes.final')}</option>
                <option value="third">{t('screens.setup.options.roundTypes.third')}</option>
                <option value="friendly">{t('screens.setup.options.roundTypes.friendly')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t('screens.setup.fields.referee')}
              </label>
              <input
                type="text"
                value={formData.refereeName}
                onChange={(e) => handleInputChange('refereeName', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 bg-gray-50 focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors font-semibold"
          >
            {t('modals.editMatch.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 transition-colors font-semibold"
          >
            {t('modals.editMatch.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
